import { Info } from '@mui/icons-material';
import { Box, Switch, Tooltip, TooltipProps, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

interface CompactToggleProps {
    labelId: string;
    tooltipId: string;
    /** Interpolation values shared by the label and tooltip messages (e.g.
     *  a structure name), for rows whose text names a live object. */
    messageValues?: Record<string, string | number>;
    value: boolean;
    onChange: (val: boolean) => void;
    /** Disables the switch and the row click; the info icon stays hoverable
     *  so the reason (carried by the surrounding UI) stays discoverable. */
    disabled?: boolean;
    /** Test id on the row (the click target that toggles). */
    dataTestId?: string;
    /**
     * Let the label wrap onto more lines when the row is narrow, instead
     * of holding one line and pushing what follows past the edge. For a
     * panel column; a tool tray keeps its one line.
     */
    wrapLabel?: boolean;
    /**
     * Which way the explanation opens; `bottom` unless given. Never toward the
     * switch — that is the control it explains.
     */
    tooltipPlacement?: TooltipProps['placement'];
    /** Spread the row across its container, the switch at the end. */
    fullWidth?: boolean;
}

/**
 * A label + switch row whose explanation hangs off an adjacent Info icon
 * rather than wrapping the row in a Tooltip.
 *
 * A row-wide tooltip would open over the very switch the user is reaching
 * for, hiding what the next click is about to change. The icon is the
 * app-wide idiom (see `Inputs/InlineToggle`) and leaves the control
 * visible while it is read.
 */
export default function CompactToggle({
    labelId,
    tooltipId,
    messageValues,
    value,
    onChange,
    disabled = false,
    dataTestId,
    wrapLabel = false,
    tooltipPlacement = 'bottom',
    fullWidth = false,
}: CompactToggleProps) {
    const intl = useIntl();
    // Resolved to a string rather than left as a <FormattedMessage> node
    // because it is handed to a Tooltip title, not rendered as a child.
    const title = intl.formatMessage({ id: tooltipId }, messageValues);
    return (
        <Box
            onClick={() => {
                if (!disabled) {
                    onChange(!value);
                }
            }}
            data-testid={dataTestId}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                ...(wrapLabel && { minWidth: 0, flex: 1 }),
            }}
        >
            <Typography
                variant="body2"
                fontSize={11}
                noWrap={!wrapLabel}
                color="rgba(255, 255, 255, 0.85)"
                sx={{ userSelect: 'none' }}
            >
                <FormattedMessage id={labelId} values={messageValues} />
            </Typography>
            <Tooltip title={title} placement={tooltipPlacement}>
                {/* Reading the explanation must not also flip the setting:
                    the icon sits inside the row's click target, so a click
                    that only meant to dismiss the tooltip would otherwise
                    toggle a clinical parameter. */}
                <Info
                    tabIndex={0}
                    role="button"
                    aria-label={title}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ fontSize: 12, opacity: 0.6, flexShrink: 0 }}
                />
            </Tooltip>
            {/* Regression: the row's onClick already covers the switch, so
                a click on the thumb reports the change TWICE (once from the
                input's change event, once from the bubbled row click). Both
                calls carry the same value, which hides it from consumers
                that only flip a boolean — but one whose handler is not
                idempotent (the threshold-segmentation region restriction
                seeds and clears a shared box) has the second call undo the
                first. The row owns the change; stop the click here. */}
            <Switch
                checked={value}
                disabled={disabled}
                sx={fullWidth ? { ml: 'auto' } : undefined}
                onClick={(e) => e.stopPropagation()}
                onChange={(_, checked) => onChange(checked)}
            />
        </Box>
    );
}
